// IMPROVEME(TEA-1016): migrate to shared utils — see follow-up to thymebox-event-service PR.
//   - _sendMonitoringEventToDatadog → @thyme/libs/src/events/datadogRumEvent
//   - _sendMonitoringEventToEventService → @thyme/libs/src/events/eventServiceClient (createEventServiceClient)
// The logged-in/non-logged-in/UTM/external-user enrichment stays here; only the
// transport primitives move.
import { datadogRum } from '@datadog/browser-rum'
import { ThymeEventError } from '@thyme/libs/src/errors/errors'
import { getLogger } from '@thyme/libs/src/logging/logger'
import { MonitoringEvent, MonitoringEventName } from '@/libs/events/types'
import { getAppConfig, isLocalEnv } from '../config/config'
import { getExternalUserIdFromSessionStorage } from './externalUser'
import { getUTMFromSessionStorage } from './utm'

const SESSION_ID_PLACEHOLDER = '00000000-0000-0000-0000-000000000000'

/**
 * Sends a monitoring event for a logged-in user.
 * This function is used when the entityId is available (i.e. the user is authenticated).
 * @param event
 * @param entityId
 * @param options
 * @param options.skipEventService
 */
export function sendLoggedInMonitoringEvent(
  event: MonitoringEvent,
  entityId: string,
  options?: { skipEventService?: boolean }
) {
  void _sendMonitoringEvent(event, entityId, options)
}

/**
 * Sends a monitoring event for a non-logged-in user.
 * This function is used when the entityId is not available (i.e. the user is not authenticated).
 * @param event
 */
export function sendNonLoggedInMonitoringEvent(event: MonitoringEvent) {
  void _sendMonitoringEvent(event)
}

/**
 * Send a monitoring event, using the appropriate function based on whether
 * an entityId is available (i.e. the user is authenticated).
 * @param event
 * @param entityId
 */
export function sendMonitoringEvent(event: MonitoringEvent, entityId: string) {
  if (entityId) {
    sendLoggedInMonitoringEvent(event, entityId)
  } else {
    sendNonLoggedInMonitoringEvent(event)
  }
}

/**
 * Trivial wrapper around rum events so we can easily swap out
 * to a different events backend if needed.
 * @param event
 * @param entityId
 * @param options
 * @param options.skipEventService
 */
async function _sendMonitoringEvent(
  event: MonitoringEvent,
  entityId?: string,
  options?: { skipEventService?: boolean }
) {
  const body = {
    ...(entityId && { entityId }),
    ...getUTMFromSessionStorage(),
    ...getExternalUserIdFromSessionStorage(),
    ...event.data,
  }

  _sendMonitoringEventToDatadog(event.event, body)

  if (!options?.skipEventService) {
    await _sendMonitoringEventToEventService(event, body)
  }
}

/**
 *
 * @param event_name
 * @param data
 */
function _sendMonitoringEventToDatadog(
  event_name: MonitoringEventName,
  data: Record<string, any>
) {
  const logBody = { event_name, data }
  if (!isLocalEnv()) {
    getLogger().info('Sending monitoring event to Datadog', logBody)
    datadogRum.addAction(event_name, data)
  } else {
    getLogger().debug(
      'Local env detected, not sending event to Datadog',
      logBody
    )
  }
}

/**
 * Make a request to event service with event and data
 * @param event
 * @param data
 */
async function _sendMonitoringEventToEventService(
  event: MonitoringEvent,
  data: Record<string, any>
): Promise<void> {
  const config = getAppConfig()
  const accessToken = config.eventService.apiKey
  const sessionId =
    datadogRum.getInternalContext()?.session_id ?? SESSION_ID_PLACEHOLDER

  if (!isLocalEnv()) {
    getLogger().info('Sending monitoring event to Event Service', {
      event_name: event.event,
      data,
    })
    try {
      await fetch(config.eventService.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          event: event.event,
          data,
          sessionId: sessionId,
        }),
      })
    } catch (err) {
      const error = ThymeEventError.fromError(err)
      getLogger().error(
        'Error sending monitoring event to Event Service',
        error
      )
    }
  } else {
    getLogger().debug(
      'Local env detected, not sending event to Event Service',
      {
        event_name: event.event,
        data,
      }
    )
  }
}
